import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '@/contexts/AuthContext';
import { Loader2, Check } from 'lucide-react';
import BackNavButton from '@/components/BackNavButton';
import { toast } from 'sonner';

export default function MeetingPrefs() {
  const navigate = useNavigate();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    Promise.all([
      api.get('/meeting-prefs/options'),
      api.get('/profile'),
    ]).then(([qr, me]) => {
      setQuestions(qr.data.questions);
      const prefs = me.data?.meeting_prefs || me.data?.profile?.meeting_prefs || {};
      setAnswers(prefs);
    }).catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const setSingle = (key, value) => setAnswers(p => ({ ...p, [key]: value }));
  const toggleMulti = (key, value) => {
    setAnswers(p => {
      const cur = Array.isArray(p[key]) ? p[key] : [];
      return { ...p, [key]: cur.includes(value) ? cur.filter(v => v !== value) : [...cur, value] };
    });
  };

  const save = async () => {
    setSaving(true);
    try {
      await api.put('/profile', { meeting_prefs: answers });
      toast.success('Preferences saved');
      navigate(-1);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <div className="min-h-screen bg-white flex items-center justify-center"><Loader2 className="w-7 h-7 animate-spin text-[#1E4FD8]" /></div>;

  const completedCount = questions.filter(q => {
    const v = answers[q.key];
    return q.type === 'multi' ? Array.isArray(v) && v.length > 0
         : q.type === 'free_text' ? !!(v && v.trim())
         : !!v;
  }).length;

  return (
    <div className="min-h-screen bg-white pb-[120px]" data-testid="meeting-prefs-page">
      <div className="px-5 pt-8 pb-3 flex items-center gap-3">
        <BackNavButton />
        <h1 className="font-brand text-[28px] font-[600] tracking-tight leading-none">Meeting Preferences</h1>
      </div>

      <div className="px-5 pb-3">
        <p className="text-[13px] text-[#4B4B52] leading-relaxed">
          Help us match you for <span className="font-semibold text-[#0F0F10]">real-life meet-ups</span>, not just chats. Skip anything that doesn't apply.
        </p>
        <div className="mt-3 flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wide">
          <span className="text-[#8A8A93]">{completedCount}/{questions.length} answered</span>
          <div className="flex-1 h-1 bg-black/[0.05] rounded-full overflow-hidden">
            <div className="h-full brand-gradient transition-all" style={{ width: `${(completedCount / Math.max(questions.length, 1)) * 100}%` }} />
          </div>
        </div>
      </div>

      <div className="px-5 space-y-5 mt-2">
        {questions.map(q => (
          <div key={q.key} className="bg-[var(--surface-2)] border border-[var(--hairline)] rounded-2xl p-4" data-testid={`mpref-${q.key}`}>
            <p className="text-[14px] font-semibold text-[#0F0F10] mb-3">{q.question}</p>

            {q.type === 'free_text' ? (
              <input
                type="text"
                value={answers[q.key] || ''}
                onChange={(e) => setSingle(q.key, e.target.value)}
                placeholder="e.g. Williamsburg, Lower East Side, Park Slope"
                data-testid={`mpref-input-${q.key}`}
                className="w-full rounded-xl border border-[var(--hairline-strong)] bg-white text-sm px-4 py-3 focus:outline-none focus:ring-2 focus:ring-[#1E4FD8]/30"
              />
            ) : q.type === 'multi' ? (
              <div className="flex flex-wrap gap-2">
                {q.options.map(o => {
                  const cur = Array.isArray(answers[q.key]) ? answers[q.key] : [];
                  const sel = cur.includes(o.value);
                  return (
                    <button key={o.value} onClick={() => toggleMulti(q.key, o.value)}
                      data-testid={`mpref-${q.key}-${o.value}`}
                      className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold border transition ${
                        sel ? 'bg-[#1E4FD8] text-white border-transparent hover:bg-[#1a45c2]' : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]'
                      }`}
                    >
                      {sel && <Check size={11} className="inline mr-1 -mt-0.5"/>}
                      {o.label}
                    </button>
                  );
                })}
              </div>
            ) : (
              <div className="flex flex-wrap gap-2">
                {q.options.map(o => {
                  const sel = answers[q.key] === o.value;
                  return (
                    <button key={o.value} onClick={() => setSingle(q.key, o.value)}
                      data-testid={`mpref-${q.key}-${o.value}`}
                      className={`px-3.5 py-1.5 rounded-full text-[13px] font-semibold border transition ${
                        sel ? 'bg-[#1E4FD8] text-white border-transparent hover:bg-[#1a45c2]' : 'bg-white text-[#4B4B52] border-[var(--hairline-strong)] hover:bg-black/[0.02]'
                      }`}
                    >
                      {sel && <Check size={11} className="inline mr-1 -mt-0.5"/>}
                      {o.label}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        ))}
      </div>

      <div className="fixed bottom-[80px] left-0 right-0 px-5 z-30">
        <div className="max-w-lg mx-auto bg-white/95 backdrop-blur-md p-3 rounded-2xl border border-[var(--hairline)] shadow-lg">
          <button onClick={save} disabled={saving} data-testid="save-meeting-prefs"
            className="w-full py-3 rounded-full brand-gradient text-white text-sm font-semibold inline-flex items-center justify-center gap-2 brand-glow-sm hover:scale-[1.01] disabled:opacity-50 transition-all">
            {saving ? <Loader2 size={14} className="animate-spin"/> : null}
            Save preferences
          </button>
        </div>
      </div>
    </div>
  );
}
